import Container from "@mui/material/Container";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import { NotAuthorized } from "_shared/_errors/_views/not-authorized";
import { useDialog } from "_shared/_providers/dialogs/dialog";
import { RootState, useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import Guid from "_shared/guids/guid";
import axios, { CanceledError } from "axios";
import ViewCompanyProfile from "marketplace/companies/profiles/_views/view-company-profile";
import { getSessionPermissions, populateSessionPermissions } from "permissions/_stores/permissions-redux-slice";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useLoaderData, useNavigate } from "react-router-dom";
import { useSession } from "users/__session/session-context";
import SuperUserAPIService from "users/super-users/_api/super-user-api-service";
import EntitiesPage from "users/super-users/_views/entity-administration/entities-page";
import { PageTab } from "users/super-users/_views/super-user-administration-panel-tabs";
import SystemNotifications from "users/super-users/_views/system-notifications/system-notifications-table";
import ActiveUserInvitationsPage from "users/super-users/_views/user-administration/user-invitations-page";
import UsersAdmin from "users/super-users/_views/user-administration/users-table";

const PageContainer = styled(Container)(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    padding: 0,
  },
  display: "flex",
  flexDirection: "column",
  height: "80vh",
  marginTop: theme.spacing(6),
  maxWidth: "70rem",
}));
const PageTitle = styled(Typography)({
  marginBottom: "2rem",
  textAlign: "center",
});
const Version = styled(Typography)(({ theme }) => ({
  color: theme.palette.grey[500],
  fontSize: "1.2em",
  marginTop: theme.spacing(2),
  textAlign: "center",
}));

export default function SuperUserDashboard() {
  const routeParams = useLoaderData();
  const [activeTab, setActiveTab] = React.useState<PageTab>(PageTab.Users);
  const [appVersion, setAppVersion] = useState<string>("Unknown");

  const session = useSession();
  const sessionPermissions = useSelector(getSessionPermissions);
  const sessionPermissionsLoading = useSelector((state: RootState) => state.permissions.session.loading);
  const sessionPermissionsError = useSelector((state: RootState) => state.permissions.session.error);
  const dispatch = useAttorneyHubDispatch();

  const navigate = useNavigate();
  const { openDialog } = useDialog();

  useEffect(() => {
    if (!sessionPermissions && !sessionPermissionsError && !sessionPermissionsLoading) {
      dispatch(populateSessionPermissions({ session }));
    }
  }, [sessionPermissions, sessionPermissionsError, sessionPermissionsLoading]);

  useEffect(() => {
    const tab = Object.values(PageTab).find((tab) => tab === routeParams.tab);
    if (tab) {
      setActiveTab(tab);
    } else {
      navigate(`/super-user/${PageTab.Users}`);
      return;
    }

    loadRouteProfile(routeParams.type, routeParams.id);

    function loadRouteProfile(type?: string, id?: string) {
      if (!type || !id) return;

      const guidId = new Guid(id);
      const parentPath = new URL(".", globalThis.location.origin + globalThis.location.pathname);

      if (type === "company") {
        openDialog({
          component: <ViewCompanyProfile companyId={guidId} />,
          MuiProps: {
            fullWidth: true,
            maxWidth: "lg",
          },
          contentSxProps: {
            display: "flex",
          },
          titleStyle: {
            paddingBottom: 0,
          },
          onClose: () => {
            navigate(parentPath.pathname);
          },
        });
      } else {
        console.warn(`Unknown profile type: ${type}`);
      }
    }
  }, [routeParams, activeTab]);

  useEffect(() => {
    if (!session.user?.isSuperUser) return;

    async function getAppVersion(abortController: AbortController): Promise<string | undefined> {
      try {
        const superUserService = new SuperUserAPIService(session);
        return await superUserService.getAppVersion(abortController);
      } catch (error) {
        if (error instanceof CanceledError) return;
        if (axios.isAxiosError(error)) console.error(error.message);
      }
    }
    let abortController = new AbortController();

    getAppVersion(abortController).then((version) => setAppVersion(version ?? "Unknown"));
    return () => {
      abortController.abort();
      abortController = new AbortController();
    };
  }, []);

  if (!session.user?.isSuperUser) return <NotAuthorized />;

  return (
    <PageContainer>
      <PageTitle id="super-user-dashboard-header" variant="h4">
        Super User Dashboard
      </PageTitle>
      <Tabs
        variant="fullWidth"
        indicatorColor="primary"
        value={activeTab}
        onChange={(event: React.SyntheticEvent, newValue: string) => navigate(`/super-user/${newValue}`)}
        textColor="primary"
      >
        <Tab id="super-user-users-tab" label="Users" value="users" />
        <Tab id="super-user-entities-tab" label="Entities" value="entities" />
        <Tab id="super-user-invitations-tab" label="Invitations" value="invitations" />
        <Tab id="super-user-notifications-tab" label="System Notifications" value="system-notifications" />
      </Tabs>
      {activeTab === PageTab.Users && (
        <UsersAdmin
          onProfileView={(type?: string, id?: string) => {
            if (!type || !id) return;
            navigate(`/super-user/${PageTab.Users}/${type}/${id}`);
          }}
        />
      )}
      {activeTab === PageTab.Entities && <EntitiesPage />}
      {activeTab === PageTab.Invitations && <ActiveUserInvitationsPage />}
      {activeTab === PageTab.SystemNotifications && <SystemNotifications />}
      <Version>Version: {appVersion}</Version>
    </PageContainer>
  );
}
